import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,  // 30 second timeout for all requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('🔍 Request interceptor - Token from localStorage:', token ? 'Present' : 'Missing');
    console.log('🔍 Request URL:', config.url);
    console.log('🔍 Request headers before:', config.headers);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔍 Added Authorization header:', `Bearer ${token.substring(0, 20)}...`);
    } else {
      console.log('🔍 No token found in localStorage');
    }
    
    console.log('🔍 Request headers after:', config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          console.log('🔄 Reactive token refresh triggered by 401 error...');
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken
          }, {
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          const { access_token } = response.data;
          localStorage.setItem('token', access_token);
          
          // Notify token manager about the refresh
          window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
            detail: { access_token, source: 'reactive' } 
          }));
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
          
        } catch (refreshError) {
          console.error('🔄 Reactive token refresh failed:', refreshError);
          
          // Notify about refresh failure
          window.dispatchEvent(new CustomEvent('tokenRefreshFailed', { 
            detail: { error: refreshError.message, source: 'reactive' } 
          }));
          
          // Refresh failed, logout user
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, logout user
        console.log('🔄 No refresh token available, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    
    return api.post('/auth/login/access-token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }).then(response => response.data);
  },
  
  register: (userData) => 
    api.post('/auth/register', userData),
  
  getCurrentUser: () => 
    api.get('/auth/me').then(response => response.data),
  
  refreshToken: (refreshToken) => 
    api.post('/auth/refresh', { refresh_token: refreshToken }).then(response => response.data),
  
  logout: (refreshToken) => 
    api.post('/auth/logout', { refresh_token: refreshToken }).then(response => response.data),
};

// Jobs API
export const jobsAPI = {
  getJobs: (params = {}) => 
    api.get('/jobs/', { params }).then(response => response.data),
  
  getMyJobs: (params = {}) => 
    api.get('/jobs/my-jobs', { params }).then(response => response.data),
  
  getJob: (id) => 
    api.get(`/jobs/${id}`).then(response => response.data),
  
  getJobLeads: (id, params = {}) => 
    api.get(`/jobs/${id}/leads`, { params }).then(response => response.data),
  
  getJobLeadsCount: (id, params = {}) => 
    api.get(`/jobs/${id}/leads/count`, { params }).then(response => response.data),
  
  getAllLeadIds: (id, params = {}) => 
    api.get(`/jobs/${id}/leads/ids`, { params }).then(response => response.data),
  
  getJobLead: (jobId, leadId) => 
    api.get(`/jobs/${jobId}/leads/${leadId}`).then(response => response.data),
  
  getJobStats: (id) => 
    api.get(`/jobs/${id}/stats`).then(response => response.data),
  
  exportJobLeadsExcel: (id, params = {}) => 
    api.get(`/jobs/${id}/export/excel`, { params, responseType: 'blob' }),
  
  exportJobLeadsCSV: (id, params = {}) => 
    api.get(`/jobs/${id}/export/csv`, { params, responseType: 'blob' }),
  
  createJob: (jobData) => 
    api.post('/jobs/', jobData).then(response => response.data),
  
  updateJob: (id, jobData) => 
    api.put(`/jobs/${id}`, jobData).then(response => response.data),
  
  deleteJob: (id) => 
    api.delete(`/jobs/${id}`).then(response => response.data),
  
  startJob: (id) => 
    api.post(`/jobs/${id}/start`).then(response => response.data),
  
  // Skip/Unskip Lead Operations
  skipLead: (jobId, leadId, reason, notes = null) => 
    api.post(`/jobs/${jobId}/leads/${leadId}/skip`, { reason, notes }).then(response => response.data),
  
  unskipLead: (jobId, leadId, notes = null) => 
    api.post(`/jobs/${jobId}/leads/${leadId}/unskip`, notes ? { notes } : {}).then(response => response.data),
  
  skipLeadsBatch: (jobId, leadIds, reason, notes = null) => 
    api.post(`/jobs/${jobId}/leads/skip-batch`, { lead_ids: leadIds, reason, notes }).then(response => response.data),
  
  unskipLeadsBatch: (jobId, leadIds, notes = null) => 
    api.post(`/jobs/${jobId}/leads/unskip-batch`, { lead_ids: leadIds, notes }).then(response => response.data),
  
  uploadFile: (file, jobData) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', jobData.name);
    formData.append('description', jobData.description || '');
    formData.append('job_type', jobData.job_type);
    
    return api.post('/jobs/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data);
  },
};

// API Usage API
export const apiUsageAPI = {
  getUsageLogs: (params = {}) => 
    api.get('/api-usage/', { params }).then(response => response.data),
  
  getUsageSummary: () => 
    api.get('/api-usage/summary').then(response => response.data),
  
  getJobUsage: (jobId) => 
    api.get(`/api-usage/job/${jobId}`).then(response => response.data),
};

// Lead Notes API
export const leadNotesAPI = {
  createNote: (jobId, leadId, noteData) => 
    api.post(`/jobs/${jobId}/leads/${leadId}/notes`, noteData).then(response => response.data),
  
  getNotes: (jobId, leadId) => 
    api.get(`/jobs/${jobId}/leads/${leadId}/notes`).then(response => response.data),
  
  getPersonalNotes: (jobId, leadId) => 
    api.get(`/jobs/${jobId}/leads/${leadId}/notes/personal`).then(response => response.data),
  
  getSharedNotes: (jobId, leadId) => 
    api.get(`/jobs/${jobId}/leads/${leadId}/notes/shared`).then(response => response.data),
  
  updateNote: (noteId, noteData) => 
    api.put(`/notes/${noteId}`, noteData).then(response => response.data),
  
  deleteNote: (noteId) => 
    api.delete(`/notes/${noteId}`).then(response => response.data),
};

// Lead Flags API
export const leadFlagsAPI = {
  createFlag: (flagData) => 
    api.post('/flags/', flagData).then(response => response.data),
  
  getFlags: () => 
    api.get('/flags/').then(response => response.data),
  
  updateFlag: (flagId, flagData) => 
    api.put(`/flags/${flagId}`, flagData).then(response => response.data),
  
  deleteFlag: (flagId) => 
    api.delete(`/flags/${flagId}`).then(response => response.data),
  
  assignFlag: (jobId, leadId, flagId) => 
    api.post(`/jobs/${jobId}/leads/${leadId}/flags/${flagId}`).then(response => response.data),
  
  removeFlag: (jobId, leadId, flagId) => 
    api.delete(`/jobs/${jobId}/leads/${leadId}/flags/${flagId}`).then(response => response.data),
  
  getLeadFlags: (jobId, leadId) => 
    api.get(`/jobs/${jobId}/leads/${leadId}/flags`).then(response => response.data),
};

// Custom Export API
export const customExportAPI = {
  exportLead: (jobId, leadId, fields, format, detailLevel = 'compact') => 
    api.post(`/jobs/${jobId}/leads/${leadId}/custom-export`, 
      { fields, format, detail_level: detailLevel }, 
      { responseType: format === 'html' ? 'blob' : 'blob' }),
  
  exportBatch: (jobId, leadIds, fields, format, detailLevel = 'compact') => 
    api.post(`/jobs/${jobId}/leads/custom-export-batch`, 
      { lead_ids: leadIds, fields, format, detail_level: detailLevel }, 
      { responseType: format === 'html' ? 'blob' : 'blob' }),
};

// GeBIZ API
export const gebizAPI = {
  // Get GeBIZ awards for a job with optional filtering
  getAwards: (jobId, params = {}) => 
    api.get(`/jobs/${jobId}/gebiz-awards`, { params }).then(response => response.data),

  // Get total count of GeBIZ awards (with same filters as getAwards)
  getAwardsCount: (jobId, params = {}) =>
    api.get(`/jobs/${jobId}/gebiz-awards/count`, { params }).then(response => response.data),

  // Get summary statistics for GeBIZ awards
  getAwardsSummary: (jobId) => 
    api.get(`/jobs/${jobId}/gebiz-awards/summary`).then(response => response.data),
  
  // Admin review for a GeBIZ award match
  reviewAward: (jobId, awardId, reviewData) => 
    api.patch(`/jobs/${jobId}/gebiz-awards/${awardId}/review`, reviewData).then(response => response.data),
  
  // Get processing logs for a job
  getProcessingLogs: (jobId, params = {}) => 
    api.get(`/jobs/${jobId}/processing-logs`, { params }).then(response => response.data),
  
  // Get API usage for a specific lead
  getLeadApiUsage: (jobId, leadId) => 
    api.get(`/jobs/${jobId}/leads/${leadId}/api-usage`).then(response => response.data),
  
  // Get GeBIZ awards linked to a specific lead
  getLeadAwards: (jobId, leadId) =>
    api.get(`/jobs/${jobId}/leads/${leadId}/awards`).then(response => response.data),
  
  // Search leads by company name
  searchLeads: (query, limit = 10) => 
    api.get('/leads/search', { params: { q: query, limit } }).then(response => response.data),
  
  // List PDFs for a job with processing status
  getPDFs: (jobId) => 
    api.get(`/jobs/${jobId}/gebiz-awards/pdfs`).then(response => response.data),
  
  // Trigger PDF extraction (Celery task)
  triggerExtraction: (jobId, files = null) => 
    api.post(`/jobs/${jobId}/gebiz-awards/extract`, files ? { files } : null).then(response => response.data),
  
  // Trigger company matching (Celery task)
  triggerMatching: (jobId) => 
    api.post(`/jobs/${jobId}/gebiz-awards/match`).then(response => response.data),
  
  // Trigger lead enrichment (Celery task)
  triggerEnrichment: (jobId, leadId, skipRecordOwlAuth = false) =>
    api.post(`/jobs/${jobId}/leads/${leadId}/enrich`, { skip_recordowl_auth: skipRecordOwlAuth }).then(response => response.data),
};

// SGPBusiness Alert List API
export const sgpbusinessAPI = {
  // Overview status (last scrape, tracked count, pending review, etc.)
  getStatus: (jobId) =>
    api.get(`/jobs/${jobId}/sgpbusiness/status`).then(response => response.data),

  // Scrape the alert list homepage and persist results
  scrape: (jobId) =>
    api.post(`/jobs/${jobId}/sgpbusiness/scrape`).then(response => response.data),

  // Match scraped companies against the job's leads (dryRun previews, no writes)
  match: (jobId, dryRun = false) =>
    api.post(`/jobs/${jobId}/sgpbusiness/match`, null, { params: { dry_run: dryRun } }).then(response => response.data),

  // Fuzzy matches awaiting admin review
  getReviewQueue: (jobId) =>
    api.get(`/jobs/${jobId}/sgpbusiness/review`).then(response => response.data),

  // Admin review action: confirm / assign_existing / create_new / skip
  reviewCompany: (jobId, companyId, payload) =>
    api.patch(`/jobs/${jobId}/sgpbusiness/${companyId}/review`, payload).then(response => response.data),
};

// Session API - for external service session management
export const sessionAPI = {
  // Get RecordOwl session status (from API server - may not have disk access on Render)
  getRecordOwlStatus: () => 
    api.get('/session/recordowl/status').then(response => response.data),
  
  // Get RecordOwl session status (public endpoint, no auth required)
  getRecordOwlStatusPublic: () => 
    api.get('/session/recordowl/status/public').then(response => response.data),
  
  // Get RecordOwl session status from Celery worker (accurate check with disk access)
  // This is the preferred method as the worker has access to the persistent disk
  getRecordOwlStatusFromWorker: (timeout = 10) => 
    api.get('/session/recordowl/status/worker', { params: { timeout } }).then(response => response.data),
};

// Intervention API - for Browserless LiveURL intervention handling
export const interventionAPI = {
  // Check for pending intervention during enrichment
  checkPending: (jobId, leadId) => 
    api.get('/intervention', { params: { job_id: jobId, lead_id: leadId } }).then(response => response.data),
  
  // Check pending (public - no auth required)
  checkPendingPublic: (jobId, leadId) => 
    api.get('/intervention/public', { params: { job_id: jobId, lead_id: leadId } }).then(response => response.data),
  
  // Mark intervention as completed (user finished LiveURL verification)
  markComplete: (interventionId) => 
    api.post(`/intervention/${interventionId}/complete`).then(response => response.data),
  
  // Cancel/skip intervention
  cancel: (interventionId) => 
    api.delete(`/intervention/${interventionId}`).then(response => response.data),
};

// Enrichment Session API - for Browserless batch session management
export const enrichmentSessionAPI = {
  // Start a new enrichment session for a job
  // Returns LiveURL for user to complete Google OAuth
  startSession: (jobId, liveUrlTimeoutMinutes = 5) => 
    api.post('/enrichment/session/start', { 
      job_id: jobId, 
      live_url_timeout_minutes: liveUrlTimeoutMinutes 
    }).then(response => response.data),
  
  // Verify that login has been completed for a session
  verifyLogin: (sessionId) => 
    api.post(`/enrichment/session/${sessionId}/verify`).then(response => response.data),
  
  // Get session status
  getSession: (sessionId) => 
    api.get(`/enrichment/session/${sessionId}`).then(response => response.data),
  
  // Get active session for a job (if any)
  getJobSession: (jobId) => 
    api.get(`/enrichment/session/job/${jobId}`).then(response => response.data),
  
  // End a session
  endSession: (sessionId) => 
    api.delete(`/enrichment/session/${sessionId}`).then(response => response.data),
  
  // List all active sessions (admin/debug)
  listSessions: () => 
    api.get('/enrichment/sessions').then(response => response.data),
  
  // Public status check (no auth required)
  getSessionStatusPublic: (sessionId) => 
    api.get(`/enrichment/session/${sessionId}/status/public`).then(response => response.data),
  
  // Check Redis cookie validity for a job
  // Cookies are stored in Redis after login and remain valid for ~2 hours
  // This allows enrichment even after Browserless session times out (15 min)
  checkCookies: (jobId) => 
    api.get(`/enrichment/cookies/job/${jobId}`).then(response => response.data),
  
  // Delete Redis cookies for a job (force re-authentication)
  deleteCookies: (jobId) => 
    api.delete(`/enrichment/cookies/job/${jobId}`).then(response => response.data),
};

// Tasks API - for Celery task progress tracking
export const tasksAPI = {
  // Get all tasks for a job
  getJobTasks: (jobId, params = {}) => 
    api.get(`/jobs/${jobId}/tasks`, { params }).then(response => response.data),
  
  // Get task summary for a job
  getJobTasksSummary: (jobId) => 
    api.get(`/jobs/${jobId}/tasks/summary`).then(response => response.data),
  
  // Get the currently active task for a job (for polling)
  getActiveTask: (jobId, includeLogs = true) => 
    api.get(`/jobs/${jobId}/tasks/active`, { params: { include_logs: includeLogs } }).then(response => response.data),
  
  // Get a specific task by database ID
  getTask: (taskId) => 
    api.get(`/tasks/${taskId}`).then(response => response.data),
  
  // Get a task by Celery task UUID
  getTaskByCeleryId: (celeryTaskId) => 
    api.get(`/tasks/celery/${celeryTaskId}`).then(response => response.data),
  
  // Get logs for a task (supports incremental polling with since_id)
  getTaskLogs: (taskId, params = {}) => 
    api.get(`/tasks/${taskId}/logs`, { params }).then(response => response.data),
};

export default api; 